import { apiDate, dateKey } from "./slotSelection";

/**
 * Montants et commande du récapitulatif (design Checkout.dc.html, écran 03).
 *
 * Les règles sont reprises à l'identique de l'ancien checkout
 * (Checkout/index.jsx et Modals/ConfirmOrder) : seule l'interface change.
 * Le serveur recalcule le montant ; celui affiché ici sert à l'utilisateur
 * et à savoir si le portefeuille couvre la commande.
 */

const cents = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * `cart.promo` reçoit aussi le corps d'une réponse 422 (voir setPromo) :
 * seul un code avec un identifiant et une remise est réellement appliqué.
 */
export const isPromoApplied = (promo) =>
  Boolean(promo?.id) && promo?.discount !== undefined && promo?.discount !== null;

/** Remise en euros : montant fixe plafonné au sous-total, ou pourcentage. */
export const promoDiscount = (subtotal, promo) => {
  if (!isPromoApplied(promo)) {
    return 0;
  }

  const base = Number(subtotal) || 0;
  const discount = Number(promo.discount) || 0;

  return promo.fixed_amount
    ? cents(Math.min(discount, base))
    : cents((base * discount) / 100);
};

/**
 * Frais liés à l'adresse : frais de livraison pour le domicile, frais de
 * service quand le point de collecte en a. Un frais nul n'apparaît pas.
 */
export const addressFees = (address, isHome) => {
  const fees = [];
  const delivery = Number(address?.delivery_fee) || 0;
  const service = Number(address?.service_fee) || 0;

  if (isHome && delivery > 0) {
    fees.push({ kind: "delivery", amount: cents(delivery) });
  }

  if (service > 0) {
    fees.push({ kind: "service", amount: cents(service) });
  }

  return fees;
};

export const orderTotals = ({ subtotal, promo, address, isHome }) => {
  const base = cents(subtotal);
  const discount = promoDiscount(base, promo);
  const fees = addressFees(address, isHome);
  const total = cents(
    base - discount + fees.reduce((sum, fee) => sum + fee.amount, 0)
  );

  return { subtotal: base, discount, fees, total: Math.max(0, total) };
};

/**
 * Corps de POST /customer/orders, champ pour champ comme ConfirmOrder.
 * `payment` : { type: "Wallet" } ou { type: "Card", cardId }.
 */
export const buildOrderPayload = ({
  pickup,
  pickupSlot,
  delivery,
  deliverySlot,
  address,
  isHome,
  promo,
  cartItems,
  description,
  payment,
}) => {
  const order = {
    pickup_date: apiDate({
      day: pickup?.date?.day,
      key: dateKey(pickup?.date?.date),
    }),
    delivery_date: apiDate({
      day: delivery?.date?.day,
      key: dateKey(delivery?.date?.date),
    }),
    pickup_slot: pickupSlot,
    delivery_slot: deliverySlot,
    orderable_id: address?.id || null,
    payment_type: payment.type,
    orderable_type: isHome ? "DeliveryAddress" : "CollectionPoint",
    promo_code_id: isPromoApplied(promo) ? promo.id : null,
    product_orders_attributes: (cartItems || []).map((item) => ({
      product_id: item.id,
      quantity: item.quantity,
    })),
    description: description || "",
  };

  if (payment.type === "Card") {
    order.payment_method_id = payment.cardId;
  }

  return { order };
};

/**
 * Le serveur répond `{ message: { champ: [raisons] } }` ou une chaîne.
 * Même mise en forme que ConfirmOrder : « orderable » devient « delivery
 * address » et la clé générique « order » disparaît.
 */
export const orderErrorMessage = (data) => {
  const message = data?.message ?? data?.errors;

  if (!message) {
    return null;
  }

  if (typeof message === "string") {
    return message;
  }

  if (Array.isArray(message)) {
    return message.join(" ");
  }

  return Object.keys(message)
    .map((key) => {
      const label =
        key === "orderable" ? "delivery address" : key === "order" ? "" : key;
      const value = [].concat(message[key]).join(", ");

      return `${label.replace(/_/g, " ")} ${value}`.trim();
    })
    .join(". ");
};

/** Numéro national saisi derrière le préfixe +32, remis au format E.164. */
export const toInternationalPhone = (raw) => {
  const trimmed = String(raw || "").trim();

  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    return trimmed.replace(/\s+/g, "");
  }

  return `+32${trimmed.replace(/\s+/g, "").replace(/^0+/, "")}`;
};

/**
 * « Rue du Bailli 42, 1050 Ixelles, Belgium » → deux lignes, comme le
 * design. Une adresse sans virgule reste sur la première ligne.
 */
export const splitAddress = (text) => {
  const value = String(text || "").trim();
  const comma = value.indexOf(",");

  if (comma === -1) {
    return { line1: value, line2: "" };
  }

  return {
    line1: value.slice(0, comma).trim(),
    line2: value.slice(comma + 1).trim(),
  };
};
